import type { CellValueChangedEvent } from 'ag-grid-community'
import type { ViewHook } from 'phoenix_live_view'
import {
  allPass,
  dropWhile,
  filter,
  find,
  findIndex,
  findLast,
  forEach,
  hasProp,
  isArray,
  isBoolean,
  isNumber,
  isPlainObject,
  isString,
  map,
  pipe,
  reduce,
  takeWhile
} from 'remeda'
import type { JSONSerializable } from '../utils/cache'
import type { BadgeCellData } from '../utils/column-processors'
import {
  clearStagedEditorValue,
  createOptimisticEditorValue,
  extractEditorDisplayValue,
  extractEditorRawValue,
  isStructuredEditorCell
} from '../utils/editor-value'
import type { GridRuntime } from '../utils/runtime'
import type { StepperCellData, StepperValueItem } from '../utils/transforms'
import { isValidStepperData } from '../utils/transforms'
import { hasValueChanged, normalizeForComparison } from '../utils/validators'

/**
 * Cell Value Change Handlers for AG Grid
 * Handles cell editing, value transformation, and server synchronization
 */

// Cell value change payload structure
type CellValueChangePayload = {
  readonly id: string | number
  readonly data: Record<string, unknown>
  readonly expected_data: Record<string, unknown>
  readonly row_data: Record<string, unknown>
}

type ServerReply =
  | {
      readonly success: true
      readonly data?: Readonly<Record<string, unknown>>
    }
  | {
      readonly success: false
      readonly error?: string
      readonly data?: Readonly<Record<string, unknown>>
    }

type AuthoritativeCell =
  | { readonly status: 'absent' }
  | { readonly status: 'present'; readonly value: unknown }

const NO_AUTHORITATIVE_CELL: AuthoritativeCell = { status: 'absent' }

type SettledEditResolution =
  | { readonly status: 'accepted'; readonly value: unknown }
  | {
      readonly status: 'rejected'
      readonly authoritativeCell: AuthoritativeCell
    }

type EditResolution = { readonly status: 'pending' } | SettledEditResolution

type ActiveEdit = {
  readonly generation: number
  readonly optimisticValue: unknown
  readonly resolution: EditResolution
}

type SettledEdit = ActiveEdit & {
  readonly resolution: SettledEditResolution
}

type VisibleEdit = ActiveEdit & {
  readonly resolution: Exclude<EditResolution, { readonly status: 'rejected' }>
}

type ActiveEditChain = {
  readonly confirmedValue: unknown
  readonly edits: readonly ActiveEdit[]
}

type ActiveEditsMap = Map<string, ActiveEditChain>

type EditTimeouts = Map<number, ReturnType<typeof setTimeout>>

type CellValueChangedHandler = ((params: CellValueChangedEvent) => void) & {
  readonly cleanup: () => void
}

type HandlerLifecycle = 'active' | 'disposed'

const EDIT_TIMEOUT_MS = 30_000

const isOptional = <Value>(
  value: unknown,
  predicate: (candidate: unknown) => candidate is Value
): value is Value | undefined => value === undefined || predicate(value)

const isServerReply = (value: unknown): value is ServerReply =>
  isPlainObject(value) &&
  allPass(value, [
    (reply) => isBoolean(reply.success),
    (reply) => isOptional(reply.data, isPlainObject),
    (reply) =>
      reply.success === true
        ? reply.error === undefined
        : isOptional(reply.error, isString)
  ])

const isBadgeValue = (value: unknown): value is BadgeCellData['value'] =>
  value === null ||
  isString(value) ||
  isBoolean(value) ||
  (isNumber(value) && Number.isFinite(value))

const hasBadgeEditorValues = (
  badge: Readonly<Record<PropertyKey, unknown>>
): boolean =>
  isPlainObject(badge.editorParams) && isArray(badge.editorParams.values)

const hasBadgePresentation = (
  badge: Readonly<Record<PropertyKey, unknown>>
): boolean =>
  badge.value === null ||
  isBoolean(badge.value) ||
  hasProp(badge, 'icon') ||
  hasProp(badge, 'color')

/** Type guard to check if data is valid badge cell data */
const isValidBadgeData = (data: unknown): data is BadgeCellData =>
  isPlainObject(data) &&
  allPass(data, [
    (badge) => isBadgeValue(badge.value),
    (badge) => isString(badge.label),
    hasBadgeEditorValues,
    hasBadgePresentation
  ])

const isOneBasedIndex = (value: unknown): value is number =>
  isNumber(value) && Number.isSafeInteger(value) && value >= 1

const indexedStepperItem = (
  data: StepperCellData
): StepperValueItem | undefined =>
  isOneBasedIndex(data.value)
    ? data.editorParams?.values?.[data.value - 1]
    : undefined

const stepperItemLabel = (
  item: StepperValueItem | undefined
): string | undefined => item?.label || item?.title || item?.value

const isIndexedStepperData = (data: unknown): data is StepperCellData => {
  if (!(isValidStepperData(data) && isNumber(data.value))) {
    return false
  }

  const values = data.editorParams?.values ?? []

  if (data.value === 0) {
    return find(values, (item) => isString(item.title)) !== undefined
  }

  return (
    isOneBasedIndex(data.value) &&
    stepperItemLabel(indexedStepperItem(data)) === data.label
  )
}

const indexedStepperServerValue = (data: StepperCellData): unknown => {
  if (hasProp(data, '_serverValue')) {
    return data._serverValue
  }

  return indexedStepperItem(data)?.value ?? data.value
}

/** Transform stepper cell value */
const transformStepperValue = (
  newValue: string,
  oldValue: unknown
): unknown => {
  if (!isValidStepperData(oldValue)) {
    return newValue
  }

  // If newValue is empty or matches the current label (no change), preserve original data
  const currentLabel = oldValue.label || ''
  if (newValue === '' || newValue === currentLabel) {
    return oldValue
  }

  const stepperValues = oldValue.editorParams?.values || []

  // Find the 1-indexed position of the matching item in the values array
  // The stepper component expects value to be the index (1-based) for rendering
  const itemIndex = findIndex(
    stepperValues,
    (item) => stepperItemLabel(item) === newValue
  )
  const matchingItem = stepperValues[itemIndex]
  const oneIndexedValue = itemIndex >= 0 ? itemIndex + 1 : 0

  // Backend expects the UUID, not the position
  const uuidValue = matchingItem?.value ?? newValue

  return {
    value: oneIndexedValue,
    _serverValue: uuidValue, // UUID for backend
    label: matchingItem?.label || matchingItem?.title || newValue,
    editable: oldValue.editable,
    editorParams: oldValue.editorParams
  }
}

/** Transform badge cell value */
const transformBadgeValue = (
  newValue: string,
  oldValue: BadgeCellData
): unknown => {
  // If newValue is empty or matches the current label (no change), preserve original data
  if (newValue === '' || newValue === oldValue.label) {
    return oldValue
  }

  const badgeValues = oldValue.editorParams?.values || []
  const matchingItem = find(badgeValues, (item) => item.label === newValue)
  const actualValue = matchingItem?.value ?? newValue

  return {
    value: actualValue,
    label: matchingItem?.label || actualValue,
    icon: matchingItem?.icon || oldValue.icon,
    color: matchingItem?.color || oldValue.color,
    editable: oldValue.editable,
    editorParams: oldValue.editorParams
  }
}

/** Transform cell value with type safety - handles stepper and badge data reconstruction */
export const transformCellValue = (
  newValue: unknown,
  oldValue?: unknown
): unknown => {
  // Preserve original value when editor cancelled/blurred without selection
  // AG Grid's Rich Select editor returns undefined when user blurs without selecting
  if (newValue === undefined && oldValue !== undefined) {
    return oldValue
  }

  if (typeof newValue !== 'string') {
    return newValue
  }

  // Steppers store a one-based display position while badges store their raw
  // scalar. Both can carry icon/color metadata, so resolve the indexed shape
  // before the badge guard.
  if (oldValue && isIndexedStepperData(oldValue)) {
    return transformStepperValue(newValue, oldValue)
  }

  if (oldValue && isValidBadgeData(oldValue)) {
    return transformBadgeValue(newValue, oldValue)
  }

  // Handle stepper data structures
  if (oldValue && isValidStepperData(oldValue)) {
    return transformStepperValue(newValue, oldValue)
  }

  // For simple values, native AG Grid editors handle mapping internally
  return newValue === '' ? null : newValue
}

/** Generate unique cell identifier for edit tracking */
const getCellId = (params: CellValueChangedEvent): string => {
  const dataId = params.node.data?.id
  const stableDataId =
    typeof dataId === 'string' || typeof dataId === 'number'
      ? dataId
      : undefined
  const rowId = params.node.id ?? stableDataId

  return `${rowId}-${params.column.getColId()}`
}

const rawEditedValue = (params: CellValueChangedEvent): unknown =>
  Reflect.has(params, 'newRawValue') ? params.newRawValue : params.newValue

type PendingCellUpdate = {
  readonly expectedValues: readonly unknown[]
}

type InternalCellUpdates = Map<string, readonly PendingCellUpdate[]>

const pendingCellUpdate = (value: unknown): PendingCellUpdate => ({
  expectedValues: [
    value,
    extractEditorDisplayValue(value),
    extractEditorRawValue(value),
    isPlainObject(value) && hasProp(value, 'label') ? value.label : value
  ]
})

const addInternalCellUpdate = (
  internalCellUpdates: InternalCellUpdates,
  cellId: string,
  value: unknown
): PendingCellUpdate => {
  const pending = pendingCellUpdate(value)
  internalCellUpdates.set(cellId, [
    ...(internalCellUpdates.get(cellId) ?? []),
    pending
  ])
  return pending
}

const removeInternalCellUpdate = (
  internalCellUpdates: InternalCellUpdates,
  cellId: string,
  consumed: PendingCellUpdate
): void => {
  const remaining = filter(
    internalCellUpdates.get(cellId) ?? [],
    (pending) => pending !== consumed
  )

  if (remaining.length === 0) {
    internalCellUpdates.delete(cellId)
  } else {
    internalCellUpdates.set(cellId, remaining)
  }
}

const matchesPendingCellUpdate = (
  params: CellValueChangedEvent,
  pending: PendingCellUpdate
): boolean => {
  const editedValue = rawEditedValue(params)

  return (
    findIndex(
      pending.expectedValues,
      (expectedValue) =>
        Object.is(editedValue, expectedValue) ||
        Object.is(params.newValue, expectedValue)
    ) !== -1
  )
}

const consumeInternalCellUpdate = (
  internalCellUpdates: InternalCellUpdates,
  cellId: string,
  params: CellValueChangedEvent
): boolean => {
  const consumed = find(internalCellUpdates.get(cellId) ?? [], (update) =>
    matchesPendingCellUpdate(params, update)
  )

  if (!consumed) {
    return false
  }

  removeInternalCellUpdate(internalCellUpdates, cellId, consumed)
  return true
}

const setCellValueWithoutHandling = (
  params: CellValueChangedEvent,
  cellId: string,
  internalCellUpdates: InternalCellUpdates,
  value: unknown
): void => {
  const pending = addInternalCellUpdate(internalCellUpdates, cellId, value)

  try {
    const changed = params.node.setDataValue(params.column.getColId(), value)

    if (changed === false) {
      removeInternalCellUpdate(internalCellUpdates, cellId, pending)
      return
    }

    // A nested setDataValue updates row data but can retain the renderer that
    // AG Grid mounted before editing stopped. Refresh this cell so its local
    // raw/display value is painted immediately.
    params.api?.refreshCells({
      columns: [params.column],
      force: true,
      rowNodes: [params.node]
    })
  } catch (_error: unknown) {
    removeInternalCellUpdate(internalCellUpdates, cellId, pending)
    console.warn('Failed to update local grid cell value')
  }
}

const extractAuthoritativeCell = (
  reply: ServerReply,
  columnId: string
): AuthoritativeCell =>
  isPlainObject(reply.data) && hasProp(reply.data, columnId)
    ? { status: 'present', value: reply.data[columnId] }
    : NO_AUTHORITATIVE_CELL

const appendPendingEdit = (
  chain: ActiveEditChain | undefined,
  generation: number,
  originalValue: unknown,
  optimisticValue: unknown
): ActiveEditChain => ({
  confirmedValue: chain ? chain.confirmedValue : originalValue,
  edits: [
    ...(chain?.edits ?? []),
    {
      generation,
      optimisticValue,
      resolution: { status: 'pending' }
    }
  ]
})

const isSettledEdit = (edit: ActiveEdit): edit is SettledEdit =>
  edit.resolution.status !== 'pending'

const hasVisibleValue = (edit: ActiveEdit): edit is VisibleEdit =>
  edit.resolution.status !== 'rejected'

const collapseSettledPrefix = (chain: ActiveEditChain): ActiveEditChain => {
  const settledEdits = takeWhile(chain.edits, isSettledEdit)
  const confirmedValue = pipe(
    settledEdits,
    reduce((value, edit) => {
      const { resolution } = edit

      return resolution.status === 'accepted'
        ? resolution.value
        : resolution.authoritativeCell.status === 'present'
          ? resolution.authoritativeCell.value
          : value
    }, chain.confirmedValue)
  )

  return {
    confirmedValue,
    edits: dropWhile(chain.edits, isSettledEdit)
  }
}

const visibleEditValue = (chain: ActiveEditChain): unknown => {
  const visibleEdit = findLast(chain.edits, hasVisibleValue)

  return visibleEdit
    ? visibleEdit.resolution.status === 'accepted'
      ? visibleEdit.resolution.value
      : visibleEdit.optimisticValue
    : chain.confirmedValue
}

type ResolvedEdit = {
  readonly chain: ActiveEditChain
  readonly visibleValue: unknown
  readonly visibleValueChanged: boolean
}

const resolveEdit = (
  chain: ActiveEditChain,
  generation: number,
  resolution: SettledEditResolution
): ResolvedEdit | undefined => {
  const editIndex = findIndex(
    chain.edits,
    (edit) =>
      edit.generation === generation && edit.resolution.status === 'pending'
  )

  if (editIndex === -1) {
    return undefined
  }

  const previousVisibleValue = visibleEditValue(chain)
  const resolvedChain = collapseSettledPrefix({
    ...chain,
    edits: map(chain.edits, (edit, index) =>
      index === editIndex ? { ...edit, resolution } : edit
    )
  })
  const visibleValue = visibleEditValue(resolvedChain)

  return {
    chain: resolvedChain,
    visibleValue,
    visibleValueChanged: !Object.is(previousVisibleValue, visibleValue)
  }
}

const settleEdit = (
  activeEdits: ActiveEditsMap,
  internalCellUpdates: InternalCellUpdates,
  params: CellValueChangedEvent,
  cellId: string,
  generation: number,
  resolution: SettledEditResolution
): void => {
  const chain = activeEdits.get(cellId)

  if (!chain) {
    return
  }

  const transition = resolveEdit(chain, generation, resolution)

  if (!transition) {
    return
  }

  if (transition.chain.edits.length === 0) {
    activeEdits.delete(cellId)
  } else {
    activeEdits.set(cellId, transition.chain)
  }

  if (transition.visibleValueChanged) {
    setCellValueWithoutHandling(
      params,
      cellId,
      internalCellUpdates,
      transition.visibleValue
    )
  }
}

const resolutionFromReply = (
  value: unknown,
  columnId: string,
  optimisticValue: unknown
): SettledEditResolution => {
  if (!isServerReply(value)) {
    return {
      status: 'rejected',
      authoritativeCell: NO_AUTHORITATIVE_CELL
    }
  }

  const authoritativeCell = extractAuthoritativeCell(value, columnId)

  if (value.success) {
    return {
      status: 'accepted',
      value:
        authoritativeCell.status === 'present'
          ? authoritativeCell.value
          : optimisticValue
    }
  }

  return { status: 'rejected', authoritativeCell }
}

const clearEditTimeout = (
  editTimeouts: EditTimeouts,
  generation: number
): void => {
  const timeout = editTimeouts.get(generation)

  if (timeout !== undefined) {
    clearTimeout(timeout)
    editTimeouts.delete(generation)
  }
}

const localDisplayValue = (
  params: CellValueChangedEvent,
  serverValue: unknown
): unknown => {
  const formatter = params.column.getColDef().valueFormatter

  return typeof formatter === 'function'
    ? formatter({ ...params, value: serverValue } as never)
    : serverValue
}

const localCellValue = (
  params: CellValueChangedEvent,
  originalValue: unknown,
  serverValue: unknown,
  transformedValue: unknown
): unknown =>
  createOptimisticEditorValue(
    originalValue,
    transformedValue,
    localDisplayValue(params, serverValue)
  )

const originalCellValue = (
  params: CellValueChangedEvent,
  columnId: string
): unknown => {
  const value = params.node.data[columnId]

  return isStructuredEditorCell(value) ? value : params.oldValue
}

const expectedServerValue = (originalValue: unknown): unknown =>
  isIndexedStepperData(originalValue)
    ? indexedStepperServerValue(originalValue)
    : extractEditorRawValue(transformCellValue(originalValue))

/** Type guard to check if value is JSON serializable */
const isJSONSerializable = (value: unknown): value is JSONSerializable =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  (typeof value === 'object' && value !== null)

/** Check if serializable values have actually changed */
const haveSerializableValuesChanged = (
  normalizedOldValue: unknown,
  normalizedNewValue: unknown
): boolean =>
  isJSONSerializable(normalizedOldValue) &&
  isJSONSerializable(normalizedNewValue)
    ? hasValueChanged(normalizedNewValue, normalizedOldValue)
    : true

/** Normalize cell values for comparison */
const normalizeCellValues = (
  oldValue: unknown,
  newValue: unknown,
  oldValueFallback?: unknown
): readonly [unknown, unknown] =>
  normalizeForComparison(
    extractEditorRawValue(transformCellValue(oldValue)),
    extractEditorRawValue(transformCellValue(newValue, oldValueFallback))
  )

/** Create cell value changed event handler with duplicate protection */
export const createCellValueChangedHandler = (
  hook: ViewHook,
  runtime: GridRuntime,
  target?: string | null,
  eventName?: string
): CellValueChangedHandler => {
  const activeEdits: ActiveEditsMap = new Map()
  const editTimeouts: EditTimeouts = new Map()
  let nextGeneration = 0
  let lifecycle: HandlerLifecycle = 'active'

  // Track local setDataValue calls so their events cannot reenter this handler.
  const internalCellUpdates: InternalCellUpdates = new Map()

  const cleanup = (): void => {
    lifecycle = 'disposed'
    pipe(
      [...editTimeouts.values()],
      forEach((timeout) => {
        clearTimeout(timeout)
      })
    )
    editTimeouts.clear()
    activeEdits.clear()
    internalCellUpdates.clear()
  }

  const handleServerReply = (
    reply: unknown,
    params: CellValueChangedEvent,
    cellId: string,
    columnId: string,
    generation: number,
    optimisticValue: unknown
  ): void => {
    clearEditTimeout(editTimeouts, generation)
    settleEdit(
      activeEdits,
      internalCellUpdates,
      params,
      cellId,
      generation,
      resolutionFromReply(reply, columnId, optimisticValue)
    )
  }

  const processValidatedCellChange = (
    params: CellValueChangedEvent,
    cellId: string,
    columnId: string,
    serverValue: unknown,
    originalValue: unknown,
    optimisticValue: unknown
  ): void => {
    nextGeneration += 1
    const generation = nextGeneration
    activeEdits.set(
      cellId,
      appendPendingEdit(
        activeEdits.get(cellId),
        generation,
        originalValue,
        optimisticValue
      )
    )

    const timeout = setTimeout(() => {
      editTimeouts.delete(generation)
      settleEdit(activeEdits, internalCellUpdates, params, cellId, generation, {
        status: 'rejected',
        authoritativeCell: NO_AUTHORITATIVE_CELL
      })
    }, EDIT_TIMEOUT_MS)
    editTimeouts.set(generation, timeout)

    const payload: CellValueChangePayload = {
      id: params.node.data.id,
      data: { [columnId]: serverValue },
      expected_data: {
        [columnId]: expectedServerValue(originalValue)
      },
      row_data: params.node.data
    }

    const callback = (reply: unknown) =>
      handleServerReply(
        reply,
        params,
        cellId,
        columnId,
        generation,
        optimisticValue
      )

    try {
      if (target && eventName) {
        hook.pushEventTo(target, eventName, payload as object, callback)
      } else if (eventName) {
        hook.pushEvent(eventName, payload, callback)
      }
    } catch (error) {
      callback(undefined)
      throw error
    }
  }

  const handleCellValueChanged = (params: CellValueChangedEvent): void => {
    if (lifecycle === 'disposed') {
      return
    }

    try {
      // Early validation - ensure required params exist
      if (!(params.node && params.column && params.node.data)) {
        return
      }

      // Skip if cell is not actually editable
      const colDef = params.column.getColDef()
      if (colDef.editable === false) {
        return
      }

      const columnId = params.column.getColId()
      const cellId = getCellId(params)

      // Skip processing if this cell is being restored (prevents infinite loop)
      if (consumeInternalCellUpdate(internalCellUpdates, cellId, params)) {
        return
      }

      clearStagedEditorValue(params.node.data, columnId)

      // Early validation checks
      if (!eventName || runtime.isBackendUpdating(params.node.data.id)) {
        return
      }

      const originalValue = originalCellValue(params, columnId)

      // Transform the raw editor value to its full data structure immediately.
      // AG Grid resolves `newValue` through valueGetter; `newRawValue` is the
      // canonical value returned by the editor.
      const transformedValue = transformCellValue(
        rawEditedValue(params),
        originalValue
      )

      // Compare normalized server values to avoid false positives
      const [normalizedOldValue, normalizedNewValue] = normalizeCellValues(
        originalValue,
        transformedValue,
        originalValue
      )

      // Check if values actually changed
      if (
        !haveSerializableValuesChanged(normalizedOldValue, normalizedNewValue)
      ) {
        // Restore original cell value in AG Grid
        // AG Grid has already set the cell to newValue (string label for badges/steppers)
        // We need to restore the original complex data structure
        if (originalValue !== params.newValue) {
          setCellValueWithoutHandling(
            params,
            cellId,
            internalCellUpdates,
            originalValue
          )
        }

        return
      }

      const serverValue = extractEditorRawValue(transformedValue)
      const optimisticValue = localCellValue(
        params,
        originalValue,
        serverValue,
        transformedValue
      )

      // Preserve raw/display metadata locally while the backend refresh is in
      // flight so per-cell editability and rendering remain stable.
      setCellValueWithoutHandling(
        params,
        cellId,
        internalCellUpdates,
        optimisticValue
      )

      // Process the validated cell change
      processValidatedCellChange(
        params,
        cellId,
        columnId,
        serverValue,
        originalValue,
        optimisticValue
      )
    } catch (error) {
      console.warn('Cell value change handler failed, skipping update', error)
    }
  }

  return Object.assign(handleCellValueChanged, { cleanup })
}

// Export types for external use
export type { ActiveEditsMap, CellValueChangePayload, ServerReply }
